import React from 'react';
import { Calendar, Tag, Hash, File, HardDrive, Clock, MapPin } from 'lucide-react';
import { ResultItemProps } from './types';
import { formatFileSize } from './utils';
import { IconRenderer } from './IconRenderer';

export function NewsResultItem({ result, onClick }: ResultItemProps) {
  return (
    <button
      onClick={onClick}
      className="w-full text-left rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
    >
      <div className="flex items-start gap-4">
        {(result.thumbnail_url || result.image_url) ? (
          <div className="flex-shrink-0 w-24 h-16 rounded-lg overflow-hidden bg-gray-100">
            <img
              src={result.thumbnail_url || result.image_url}
              alt={result.title}
              className="w-full h-full object-cover"
            />
          </div>
        ) : (
          <IconRenderer type={result.type} />
        )}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-primary-900">
            {result.title}
          </p>
          {result.description && (
            <p className="mt-1 text-sm text-secondary-500 line-clamp-2">
              {result.description}
            </p>
          )}
          <div className="mt-1 flex items-center gap-3 text-xs text-secondary-600">
            {result.date && (
              <span className="flex items-center">
                <Calendar className="w-3 h-3 mr-1" />
                {new Date(result.date).toLocaleDateString()}
              </span>
            )}
            {result.category && (
              <span className="flex items-center">
                <Tag className="w-3 h-3 mr-1" />
                {result.category}
              </span>
            )}
          </div>
        </div>
      </div>
    </button>
  );
}

export function BiddingResultItem({ result, onClick }: ResultItemProps) {
  return (
    <button
      onClick={onClick}
      className="w-full text-left rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
    >
      <div className="flex items-start">
        <IconRenderer type={result.type} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
              new Date(result.bidding_date!) > new Date() 
                ? 'bg-green-100 text-green-800' 
                : 'bg-red-100 text-red-800'
            }`}>
              <Clock className="w-3 h-3 mr-1" />
              {new Date(result.bidding_date!) > new Date() ? 'Em andamento' : 'Encerrada'}
            </span>
          </div>

          <p className="text-sm font-medium text-primary-900">
            {result.title}
          </p>
          
          {result.description && (
            <p className="mt-1 text-sm text-gray-500 line-clamp-2">
              {result.description}
            </p>
          )}
          
          <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-gray-500">
            {result.bidding_date && (
              <span className="flex items-center whitespace-nowrap">
                <Calendar className="w-3 h-3 mr-1" />
                {new Date(result.bidding_date).toLocaleString()}
              </span>
            )}
            {result.location && (
              <span className="flex items-center">
                <MapPin className="w-3 h-3 mr-1" />
                {result.location}
              </span>
            )}
          </div>
        </div>
      </div>
    </button>
  );
}

export function ServiceResultItem({ result, onClick }: ResultItemProps) {
  return (
    <button
      onClick={onClick}
      className="w-full text-left rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
    >
      <div className="flex items-start">
        <IconRenderer 
          type={result.type} 
          icon={result.icon} 
          bg_color={result.bg_color} 
          text_color={result.text_color}
        />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-primary-900">
            {result.title}
          </p>
          {result.description && (
            <p className="mt-1 text-sm text-gray-500 line-clamp-2">
              {result.description}
            </p>
          )}
        </div>
      </div>
    </button>
  );
}

export function DocumentResultItem({ result, onClick }: ResultItemProps) {
  return (
    <button
      onClick={onClick}
      className="w-full text-left rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
    >
      <div className="flex items-start gap-4">
        <IconRenderer type={result.type} />
        <div className="flex-1 min-w-0">
          {result.number && result.year && (
            <div className="flex items-center gap-2 mb-1">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                <Hash className="w-3 h-3 mr-1" />
                {result.number}/{result.year}
              </span>
              {result.subcategory && (
                <span className="text-sm text-gray-500">
                  {result.subcategory.name_singular}
                </span>
              )}
            </div>
          )}
          <p className="text-sm font-medium text-primary-900">
            {result.title}
          </p>
          {result.description && (
            <p className="mt-1 text-sm text-secondary-500 line-clamp-2">
              {result.description}
            </p>
          )}
          <div className="mt-1 flex items-center gap-3 text-xs text-gray-400">
            {result.date && (
              <span className="flex items-center">
                <Calendar className="w-3 h-3 mr-1" />
                {new Date(result.date).toLocaleDateString()}
              </span>
            )}
            {result.category && (
              <span className="flex items-center">
                <Tag className="w-3 h-3 mr-1" />
                {result.category}
              </span>
            )}
            {result.fileType && (
              <span className="flex items-center">
                <File className="w-3 h-3 mr-1" />
                {result.fileType.split('/').pop()?.toUpperCase()}
              </span>
            )}
            {result.fileSize && (
              <span className="flex items-center">
                <HardDrive className="w-3 h-3 mr-1" />
                {formatFileSize(result.fileSize)}
              </span>
            )}
          </div>
        </div>
      </div>
    </button>
  );
}